import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useRef } from "react";
import { useReactToPrint } from "react-to-print";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { getDefaultTemplate } from "./defaultTemplate";

interface ReportHeaderProps {
  logoSrc: string;
  title?: string;
  date?: string;
  client?: string;
}

interface EditablePdfGeneratorProps {
  initialHtml?: string;
  onClose?: () => void;
  reportHeader?: ReportHeaderProps;
  customHeader?: React.ReactNode;
}

export const EditablePdfGenerator = ({
  initialHtml,
  onClose,
  reportHeader,
  customHeader,
}: EditablePdfGeneratorProps) => {
  const printRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const logoPng = require("../../../assets/photos/heric.png");

  // Default header props
  const defaultHeader: ReportHeaderProps = {
    logoSrc: logoPng,
    title: "Rapport de Projet Professionnel",
    date: new Date().toLocaleDateString("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }),
  };

  const headerProps = reportHeader || defaultHeader;

  // Initialize Tiptap editor
  const editor = useEditor({
    extensions: [StarterKit],
    content: initialHtml || getDefaultTemplate(),
    editorProps: {
      attributes: {
        class: "report-editor-content",
      },
    },
  });

  // Setup react-to-print
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: "document",
  });

  if (!editor) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-neutral-100 rounded-lg shadow-2xl w-11/12 h-5/6 overflow-hidden flex flex-col">
        {/* Header with Close Button */}
        <div className="bg-white border-b border-neutral-200 px-6 py-4 flex justify-between items-center print:hidden">
          <h2 className="text-xl font-semibold text-neutral-900">
            Éditeur PDF
          </h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-neutral-500 hover:text-neutral-700 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Toolbar */}
        <div className="bg-white border-b border-neutral-200 px-6 py-3 flex gap-2 flex-wrap print:hidden">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("bold")
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            <strong>B</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("italic")
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            <em>I</em>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleStrike().run()}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("strike")
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            <s>S</s>
          </button>
          <div className="w-px bg-neutral-300 mx-1"></div>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 1 }).run()
            }
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("heading", { level: 1 })
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            H1
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 2 }).run()
            }
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("heading", { level: 2 })
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            H2
          </button>
          <button
            onClick={() =>
              editor.chain().focus().toggleHeading({ level: 3 }).run()
            }
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("heading", { level: 3 })
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            H3
          </button>
          <div className="w-px bg-neutral-300 mx-1"></div>
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("bulletList")
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            • Liste
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("orderedList")
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            1. Liste
          </button>
          <button
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              editor.isActive("blockquote")
                ? "bg-neutral-900 text-white"
                : "bg-neutral-200 text-neutral-900 hover:bg-neutral-300"
            }`}
          >
            " Citation
          </button>
          <div className="w-px bg-neutral-300 mx-1"></div>
          <button
            onClick={() => editor.chain().focus().setHorizontalRule().run()}
            className="px-3 py-1.5 rounded text-sm font-medium bg-neutral-200 text-neutral-900 hover:bg-neutral-300 transition-colors"
          >
            ─ Ligne
          </button>
          <button
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            className="px-3 py-1.5 rounded text-sm font-medium bg-neutral-200 text-neutral-900 hover:bg-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ↶ Annuler
          </button>
          <button
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            className="px-3 py-1.5 rounded text-sm font-medium bg-neutral-200 text-neutral-900 hover:bg-neutral-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            ↷ Rétablir
          </button>
        </div>

        {/* Editor Area - Scrollable with A4 page styling */}
        <div className="flex-1 overflow-auto bg-gray-100 p-8 print:p-0 print:overflow-visible print:bg-white">
          <div
            ref={printRef}
            className="bg-white shadow-2xl mx-auto print:shadow-none"
            style={{ width: "21cm", minHeight: "29.7cm", padding: "2.5cm" }}
          >
            {/* Custom or Default Header */}
            {customHeader || (
              <div className="border-b-2 border-gray-200 pb-6 mb-8">
                <div className="flex items-start justify-between">
                  {/* Logo Section */}
                  <div className="flex-shrink-0">
                    <img
                      src={headerProps.logoSrc}
                      alt="Logo"
                      className="w-32 h-auto"
                    />
                  </div>

                  {/* Report Info Section */}
                  <div className="text-right">
                    <h1 className="text-2xl font-bold text-blue-900 mb-2 font-sans">
                      {headerProps.title}
                    </h1>
                    {headerProps.date && (
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-semibold">Date :</span> {headerProps.date}
                      </p>
                    )}
                    {headerProps.client && (
                      <p className="text-sm text-gray-600">
                        <span className="font-semibold">Client :</span> {headerProps.client}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Editable Content */}
            <EditorContent editor={editor} />
          </div>
        </div>

        {/* Footer with Actions */}
        <div className="bg-white border-t border-neutral-200 px-6 py-4 flex justify-end gap-3 print:hidden">
          {onClose && (
            <SecondaryButton variant="small" onClick={onClose}>
              Annuler
            </SecondaryButton>
          )}
          <Button
            variant="small"
            label="Télécharger PDF sélectionnable"
            onClick={() => handlePrint()}
          />
        </div>

        {/* Professional Report Styles */}
        <style>{`
          /* ProseMirror Editor Styles */
          .report-editor-content {
            outline: none;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          }

          /* Headings */
          .report-editor-content h1 {
            font-size: 2.5rem;
            font-weight: 700;
            color: #1e3a8a;
            margin-top: 2rem;
            margin-bottom: 1rem;
            line-height: 1.2;
            font-family: sans-serif;
          }

          .report-editor-content h2 {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1f2937;
            margin-top: 2rem;
            margin-bottom: 0.75rem;
            padding-bottom: 0.5rem;
            border-bottom: 2px solid #f3f4f6;
            line-height: 1.3;
            font-family: sans-serif;
          }

          .report-editor-content h3 {
            font-size: 1.25rem;
            font-weight: 600;
            color: #374151;
            margin-top: 1.5rem;
            margin-bottom: 0.5rem;
            line-height: 1.4;
            font-family: sans-serif;
          }

          /* Paragraphs */
          .report-editor-content p {
            line-height: 1.7;
            color: #374151;
            margin-bottom: 1rem;
            text-align: justify;
          }

          /* Lists */
          .report-editor-content ul,
          .report-editor-content ol {
            margin-left: 1.5rem;
            margin-bottom: 1rem;
            line-height: 1.7;
            color: #374151;
          }

          .report-editor-content li {
            margin-bottom: 0.5rem;
            padding-left: 0.25rem;
          }

          .report-editor-content ul {
            list-style-type: disc;
          }

          .report-editor-content ol {
            list-style-type: decimal;
          }

          .report-editor-content ul ul,
          .report-editor-content ol ul {
            list-style-type: circle;
            margin-top: 0.5rem;
          }

          .report-editor-content ul ul ul,
          .report-editor-content ol ul ul,
          .report-editor-content ol ol ul {
            list-style-type: square;
          }

          /* Blockquote */
          .report-editor-content blockquote {
            border-left: 4px solid #3b82f6;
            padding-left: 1rem;
            margin: 1.5rem 0;
            color: #4b5563;
            font-style: italic;
            background-color: #f9fafb;
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
          }

          /* Horizontal Rule */
          .report-editor-content hr {
            border: none;
            border-top: 1px solid #d1d5db;
            margin: 2rem 0;
          }

          /* Strong and Emphasis */
          .report-editor-content strong {
            font-weight: 700;
            color: #111827;
          }

          .report-editor-content em {
            font-style: italic;
          }

          /* Links */
          .report-editor-content a {
            color: #2563eb;
            text-decoration: underline;
          }

          .report-editor-content a:hover {
            color: #1d4ed8;
          }

          /* Code */
          .report-editor-content code {
            background-color: #f3f4f6;
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-size: 0.875em;
            font-family: "Courier New", monospace;
          }

          .report-editor-content pre {
            background-color: #1f2937;
            color: #f3f4f6;
            padding: 1rem;
            border-radius: 0.5rem;
            overflow-x: auto;
            margin-bottom: 1rem;
          }

          .report-editor-content pre code {
            background-color: transparent;
            padding: 0;
            color: inherit;
          }

          /* Print Styles */
          @media print {
            @page {
              size: A4;
              margin: 0;
            }

            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }

            .print\\:hidden {
              display: none !important;
            }

            .print\\:p-0 {
              padding: 0 !important;
            }

            .print\\:overflow-visible {
              overflow: visible !important;
            }

            .print\\:shadow-none {
              box-shadow: none !important;
            }

            .print\\:bg-white {
              background-color: white !important;
            }

            /* Ensure proper page breaks */
            .report-editor-content h1,
            .report-editor-content h2,
            .report-editor-content h3 {
              page-break-after: avoid;
            }

            .report-editor-content p,
            .report-editor-content ul,
            .report-editor-content ol {
              page-break-inside: avoid;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default EditablePdfGenerator;